import DashboardGridStack from "components/hoc/DashboardGridStack";
import React from "react";
import { ChartPanel, WidgetProps } from "types";
import SensorWidget from "./SensorWidget";
import { isPSLWidget } from "utils/chart";
import ActuatorWidget from "./ActuatorWidget";
import GatewayWidget from "./GatewayWidget";
import EventWidget from "./EventWidget";
import PSLWidget from "./PSLWidget";
import SubscriptionWidget from "./SubscriptionWidget";
import { ChartSource, ChartType } from "constant/chart";

const getWidgetBySource = (
    source: ChartSource,
    chart: ChartType
): React.ComponentType<any> | null => {
    if (source === ChartSource.SUBSCRIPTION) {
        return SubscriptionWidget;
    }

    if (isPSLWidget(source, chart)) {
        return PSLWidget;
    } else if (
        source.includes("SENSOR") ||
        [
            ChartType.CARD_MULTIPLE_LINES_TYPE3,
            ChartType.CARD_MULTIPLE_LINES_TYPE2,
        ].includes(chart)
    ) {
        // Sensor Related Widgets
        // LDSU Related Widgets
        return SensorWidget;
    } else if (source.includes("ACTUATOR")) {
        return ActuatorWidget;
    } else if (source.includes("GATEWAY") || source === "STORAGE") {
        return GatewayWidget;
    } else if (
        source === ChartSource.EVENT ||
        source === ChartSource.NOTIFIER
    ) {
        return EventWidget;
    }

    return null;
};

const getGridSize = (
    chart: ChartType,
    source: ChartSource,
    panel: ChartPanel
): { height: number; minWidthPx?: number } => {


    const attributesLength = panel?.attributes?.length ?? 0;

    if (
        ["MULTIPLE_GATEWAYS", "MULTIPLE_SENSORS"].includes(source) &&
        chart === "CARD_STATUS"
    ) {
        return { height: 410 };
    }

    if (source === "MULTIPLE_GATEWAYS" && chart === "CARD_MULTIPLE_BARS") {
        return {
            height: 410,
            minWidthPx: 560,
        };
    }

    if (source === "SINGLE_GATEWAY" && chart === "CARD_GATEWAY") {
        const showLocation = panel?.attributes?.[0]?.show_location;
        return {
            height: showLocation ? 410 : 260,
        };
    }

    if (
        [
            ChartType.CARD_MULTIPLE_LINES,
            ChartType.CARD_MULTIPLE_LINES_TYPE3,
        ].includes(chart)
    ) {
        return {
            height: (attributesLength + 1) * 60,
        };
    }

    if (chart === ChartType.CARD_MULTIPLE_LINES_TYPE2) {
        return {
            height: Math.ceil(attributesLength / 2) * 170 + 120,
        };
    }

    if (chart.startsWith("CARD")) {
        return { height: 250 };
    } else if (chart.startsWith("GAUGE")) {
        return { height: 370 };
    } else if (chart.toUpperCase() === "HEATMAP") {
        return { height: 600 };
    }

    return { height: 410 };
};

const Widget = ({
    gatewaysConfig,
    zoomLevel,
    panel,
    panel: { source, chart },
    dashboardId,
    onSizeChange,
    onDelete,
    allRegistry,
    gridWrapper,
    allAlerts,
}: WidgetProps & {
    gridWrapper: React.RefObject<any>;
}) => {
    const WidgetBody = getWidgetBySource(source, chart);
    const gsSize = getGridSize(chart, source, panel);

    return (
        <>
            {WidgetBody && (
                <DashboardGridStack
                    id={panel.uuid} 
                    role="gridcell"
                    height={gsSize.height}
                    minWidthPx={gsSize.minWidthPx}
                    onSizeChange={onSizeChange}
                    gridWrapper={gridWrapper}
                >
                    <WidgetBody
                        gatewaysConfig={gatewaysConfig}
                        zoomLevel={zoomLevel}
                        panel={panel}
                        dashboardId={dashboardId}
                        onDelete={onDelete}
                        allAlerts={allAlerts}
                        allRegistry={allRegistry}
                    />
                </DashboardGridStack>
            )}
        </>
    );
};

export { CustomSpinner } from "./WidgetBox";

export default Widget;
